/**
 * Reads line-item targeting out of a DV360 COPY's `targeting_plan` sidecar.
 *
 * A copy's line-item template deliberately carries no targeting keys —
 * copy_dv360_campaign_launcher.py's _build_line_item emits 14 keys and says so
 * ("Targeting is applied post-create (not here)"). The source's targeting rides
 * alongside the template instead, as:
 *
 *   launcherResult.targeting_plan = {
 *     <sourceLineItemId>: [
 *       { targetingType: 'TARGETING_TYPE_GEO_REGION',
 *         assignedTargetingOptions: [{ geoRegionDetails: { targetingOptionId, negative? } }] },
 *       ...
 *     ]
 *   }
 *
 * applied verbatim post-create via bulkEditAssignedTargetingOptions. The refs
 * are the same source lineItemIds the review rows use, so the plan maps 1:1.
 *
 * The IMPORT path produces a different, normalized shape instead
 * (li.geoTargeting.includedCities, li.audiences.…, written by dv360ImportPlan's
 * importedTargeting). Every builder in this folder was written against that
 * shape, so a copy rendered every targeting cell empty while the data sat in
 * the same JSON one key over. These readers close that gap WITHOUT touching the
 * launch payload — see the caller-side guards, which only ever fill an absence.
 *
 * READ-ONLY SURFACES ONLY. These feed the summary TILES
 * (extractDv360CampaignConfig, summarizeDv360Result) and deliberately NOT
 * buildDv360ReviewRows' grid cells. A grid cell is an edit affordance: a
 * non-empty geo cell makes CellDetailPanel render ✎ and × on every chip
 * (DV360_LIST_FIELDS grants geoInclude add/edit/remove), and those appliers
 * only understand the normalized key. An edit runs reviewEdits' geoAdd, which
 * does `li.geoTargeting || (li.geoTargeting = {})` — creating the key while the
 * sidecar's region stays. dv360_line_item_creator.py pops geoTargeting and
 * applies it AT CREATE; dv360_api_handler applies targeting_plan AFTER create,
 * so the line item would target BOTH, and the re-render hides it. A remove is
 * worse: applyDv360Deletions bails on the missing key, so it silently does
 * nothing and the market still launches. Filling grid cells from here is only
 * safe once those cells are made read-only for a copy, or the appliers learn to
 * write the sidecar. dv360RowBuilders.copyTargetingPlan.test.js pins that.
 *
 * DISPLAY NAMES ARE NOT AVAILABLE ON A COPY. _strip_output_only_details drops
 * `displayName` from every assigned-targeting option before the copy re-POSTs
 * it (it is output-only in DV360's input contract), so a geo option arrives as
 * a bare targetingOptionId and the best label we can honestly render is
 * `geo <id>`. The copy wizard's own step-3 grid has the same limitation and
 * renders the same label; matching it is deliberate, so the two surfaces agree.
 *
 * NOTE: CopyCampaignWorkflow/copyReviewEdits.js's deriveDv360Targeting is the
 * sibling reader of this exact structure, for the copy wizard's grid. It also
 * builds removable panel entries, which the review page has no use for. The two
 * should converge on this module once that file settles; until then, any change
 * to the plan's shape has to be made in both.
 */

/** The one `<x>Details` payload of an assigned-targeting option. */
export function optionDetails(option) {
  if (!option || typeof option !== 'object') return {};
  const key = Object.keys(option)
    .find((k) => k.endsWith('Details') && option[k] && typeof option[k] === 'object');
  return key ? option[key] : {};
}

/** Every assignedTargetingOption of one targetingType, across a line item's plan. */
function optionsOfType(planEntries, types) {
  const wanted = new Set(types);
  const out = [];
  for (const entry of Array.isArray(planEntries) ? planEntries : []) {
    if (!entry || !wanted.has(entry.targetingType)) continue;
    const opts = entry.assignedTargetingOptions;
    if (Array.isArray(opts)) out.push(...opts);
  }
  return out;
}

// Every DV360 v4 targeting type that answers "where does this line item run".
//
// Taken from the PRODUCER, not from a sibling reader: copy_dv360_campaign_launcher's
// _GEO_TARGETING_TYPES is what the copy actually carries, and _extract_targeting_plan is
// type-agnostic, so anything the source had lands in the sidecar under its own type.
// The first cut of this list was copied from CopyCampaignWorkflow/copyReviewEdits.js's
// DV360_TARGETING_FIELD_TYPES.geo, which names only the first three — so a geofenced
// campaign (proximity lists / business chains, the shape a POI-heavy travel campaign
// uses) rendered an em dash while its targeting sat in the same JSON. Both lists were
// wrong, in opposite directions: the backend set omits REGIONAL_LOCATION_LIST, this one
// omitted PROXIMITY_LOCATION_LIST and BUSINESS_CHAIN. This is the union of the five.
const GEO_TYPES = [
  'TARGETING_TYPE_GEO_REGION',
  'TARGETING_TYPE_POI',
  'TARGETING_TYPE_REGIONAL_LOCATION_LIST',
  'TARGETING_TYPE_PROXIMITY_LOCATION_LIST',
  'TARGETING_TYPE_BUSINESS_CHAIN',
];

/**
 * Geo labels for one line item's plan, split by DV360's `negative` flag.
 * Returns { include: [], exclude: [] } — empty arrays when the plan has no geo,
 * so a caller can treat "no geo in the plan" and "no plan" identically.
 */
export function targetingPlanGeo(planEntries) {
  const include = [];
  const exclude = [];
  for (const option of optionsOfType(planEntries, GEO_TYPES)) {
    const d = optionDetails(option);
    // Not every geo type is keyed the same way: POI, business chain and geo region use
    // targetingOptionId, but a proximity location list uses proximityLocationListId
    // (see _LOCATION_VERIFIER_ID_KEYS in copy_dv360_campaign_launcher.py). Reading only
    // the first would label a real geofence "geo undefined". Fall back to the serialized
    // details last so an unrecognized shape is still visible rather than silently
    // dropped from the count.
    const id = d.targetingOptionId ?? d.proximityLocationListId ?? d.displayName;
    const label = d.displayName || `geo ${id != null ? String(id) : JSON.stringify(d)}`;
    (d.negative ? exclude : include).push(label);
  }
  return { include, exclude };
}

/**
 * Audience ids targeted by one line item's plan.
 *
 * audienceGroupDetails nests per-kind groups, each with a settings[] of ids.
 * Google audiences (`includedGoogleAudienceGroup`) are INTERESTS, not
 * audiences, and are counted separately by the copy grid — excluded here so the
 * "Audiences targeted" tile means what it says. Excluded groups are skipped for
 * the same reason: the tile counts what IS targeted.
 */
export const PENDING_GEO_SUFFIX = ' (resolves at launch)';

/**
 * Audience ids as the grid LABELS them — raw, unprefixed.
 *
 * targetingPlanAudienceIds below prefixes third-party ids with `tp:` so the
 * summary tile's Set cannot collide a 1P id with a 3P one. That prefix must never
 * reach a cell: CellDetailPanel splits the cell string into chips and hands the
 * chip text back on a ×, and the applier resolves it through targetingPlanEntries.
 * If the two disagree the remove finds no entry and silently does nothing.
 */
export function targetingPlanAudienceLabels(planEntries) {
  const out = [];
  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_AUDIENCE_GROUP'])) {
    for (const [groupKey, , s] of audienceSettings(optionDetails(option))) {
      if (!groupKey.startsWith('included') || groupKey === 'includedGoogleAudienceGroup') continue;
      const id = audienceSettingId(s);
      if (id) out.push(String(id));
    }
  }
  return out;
}

export function targetingPlanAudienceIds(planEntries) {
  const ids = [];
  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_AUDIENCE_GROUP'])) {
    for (const [groupKey, group] of Object.entries(optionDetails(option))) {
      if (!groupKey.startsWith('included') || groupKey === 'includedGoogleAudienceGroup') continue;
      const groups = Array.isArray(group) ? group : [group];
      for (const g of groups) {
        for (const s of (Array.isArray(g?.settings) ? g.settings : [])) {
          // DV360 spells it thirdPartyAudienceId; some processed plans carry
          // the lowercased thirdpartyAudienceId. Accept both — reading only one
          // undercounts silently, which is the failure mode this module exists
          // to fix. '1000' is the keyword-seed sentinel, never a real audience.
          const third = s?.thirdPartyAudienceId ?? s?.thirdpartyAudienceId;
          const id = s?.firstPartyAndPartnerAudienceId
            || (third != null && String(third) !== '1000' && String(third) !== 'RESOLVED'
              ? `tp:${third}` : null)
            || s?.customListId || s?.combinedAudienceId;
          if (id) ids.push(String(id));
        }
      }
    }
  }
  return ids;
}

/** Join a label list the way the grid's summary cells do. */
function joinTruncated(labels, max = 3) {
  return labels.length <= max
    ? labels.join(', ')
    : `${labels.slice(0, max).join(', ')} +${labels.length - max}`;
}

/**
 * Every display cell the review grid can fill from a COPY's targeting_plan.
 *
 * Mirrors the cell shapes buildDv360ReviewRows produces from the IMPORT shape, so a
 * copy's row reads the same as an import's. Cells with nothing to show are simply
 * absent — the caller fills only what the template left empty.
 *
 * The sibling reader is CopyCampaignWorkflow/copyReviewEdits.js's deriveDv360Targeting,
 * which derives the same structure for the copy wizard's grid and additionally builds
 * removable panel entries. Keep the two in step.
 */
export function targetingPlanCells(planEntries, geoSpec = null) {
  const cells = {};
  const hasPlan = Array.isArray(planEntries) && planEntries.length;
  const staged = geoSpec && typeof geoSpec === 'object' ? geoSpec : null;
  if (!hasPlan && !staged) return cells;

  // ── Geo, resolved (targeting_plan) + staged (geo_plan) ───────────────
  // A staged spec with NO `additive` flag is a step-1 wizard override, and
  // _resolve_and_merge_geo strips every resolved GEO_REGION before applying it —
  // so the resolved markets must NOT be listed, or the grid reports targeting the
  // launch is about to discard. An additive spec appends instead.
  const resolved = targetingPlanGeo(planEntries);
  const replaces = Boolean(staged) && !staged.additive;
  const include = replaces ? [] : [...resolved.include];
  const exclude = replaces ? [] : [...resolved.exclude];
  const mark = (n) => `${String(n).trim()}${PENDING_GEO_SUFFIX}`;
  for (const n of (staged?.included || [])) if (String(n).trim()) include.push(mark(n));
  for (const n of (staged?.excluded || [])) if (String(n).trim()) exclude.push(mark(n));
  if (include.length || exclude.length) {
    const incSummary = include.length ? joinTruncated(include) : null;
    cells.geo = incSummary
      ? (exclude.length ? `${incSummary} · excl. ${exclude.length}` : incSummary)
      : `excl. ${exclude.length} geo target${exclude.length === 1 ? '' : 's'}`;
    cells.geoIncludeList = include.length ? include.join(' · ') : '—';
    cells.geoExcludeList = exclude.length ? exclude.join(' · ') : '—';
  }

  // ── Audiences (1P/partner, third-party, custom, combined) ────────────
  const audienceIds = targetingPlanAudienceLabels(planEntries);
  if (audienceIds.length) {
    cells.audiences = `${audienceIds.length} audience${audienceIds.length === 1 ? '' : 's'}`;
    cells.audienceList = audienceIds.join(' · ');
  }

  // ── Interests = Google audiences, which nest in the SAME option ──────
  const googleIds = [];
  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_AUDIENCE_GROUP'])) {
    const group = optionDetails(option).includedGoogleAudienceGroup;
    for (const g of (Array.isArray(group) ? group : [group])) {
      for (const s of (Array.isArray(g?.settings) ? g.settings : [])) {
        if (s?.googleAudienceId) googleIds.push(String(s.googleAudienceId));
      }
    }
  }
  if (googleIds.length) {
    cells.interests = `${googleIds.length} Google audience${googleIds.length === 1 ? '' : 's'}`;
    cells.interestIds = googleIds;
  }

  // ── Keywords. Negative keyword options stay out of the positive list. ─
  const keywords = [];
  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_KEYWORD'])) {
    const d = optionDetails(option);
    if (d.keyword && !d.negative) keywords.push(String(d.keyword));
  }
  if (keywords.length) {
    cells.keywords = `${keywords.length} keyword${keywords.length === 1 ? '' : 's'}`;
    cells.keywordList = keywords.join(' · ');
    cells.keywordCount = keywords.length;
  }

  return cells;
}

// ── EDITING A COPY'S TARGETING ───────────────────────────────────────────────
// The review page edits a copy by rewriting the SIDECAR, never by creating the
// normalized li.geoTargeting / li.audiences keys. Writing those would leave the
// plan carrying both, and the launcher applies geoTargeting at create and
// targeting_plan after — the line item would target the union. Everything below
// therefore operates on targeting_plan / geo_plan, exactly as the copy wizard's
// own appliers do.

/**
 * Stable identity of one plan option, so what the panel offers to remove is
 * exactly what the remover matches. Mirrors copyReviewEdits' targetingOptionKey.
 */
export function targetingOptionKey(option) {
  const d = optionDetails(option);
  const key = d.targetingOptionId ?? d.proximityLocationListId ?? d.inventorySourceId
    ?? d.inventorySourceGroupId ?? d.keyword ?? d.deviceType ?? d.environment
    ?? d.viewability ?? d.displayName;
  return key != null ? String(key) : JSON.stringify(d);
}

/** Every audience-id setting in an AUDIENCE_GROUP option, with its group. */
function* audienceSettings(details) {
  for (const [groupKey, group] of Object.entries(details || {})) {
    for (const g of (Array.isArray(group) ? group : [group])) {
      for (const s of (Array.isArray(g?.settings) ? g.settings : [])) {
        yield [groupKey, g, s];
      }
    }
  }
}

const audienceSettingId = (s) => s?.firstPartyAndPartnerAudienceId
  ?? s?.thirdPartyAudienceId ?? s?.thirdpartyAudienceId
  ?? s?.customListId ?? s?.combinedAudienceId ?? s?.googleAudienceId;

/**
 * The removable entries behind each targeting cell, keyed by the panel's field
 * name. The panel renders these and hands back (field, label) on a ×; the
 * remover below resolves the label through the SAME derivation, so labels can
 * never drift between what was offered and what is matched.
 */
export function targetingPlanEntries(planEntries, geoSpec = null) {
  const entries = {};
  const push = (field, entry) => { (entries[field] ||= []).push(entry); };
  const staged = geoSpec && typeof geoSpec === 'object' ? geoSpec : null;
  const replaces = Boolean(staged) && !staged.additive;

  // A replacing (step-1) override discards the resolved markets at launch, so they
  // are not offered — a × on something the launch already drops does nothing.
  if (!replaces) {
    for (const option of optionsOfType(planEntries, GEO_TYPES)) {
      const d = optionDetails(option);
      const id = d.targetingOptionId ?? d.proximityLocationListId ?? d.displayName;
      push('geo', {
        label: d.displayName || `geo ${id != null ? String(id) : JSON.stringify(d)}`,
        targetingType: geoTypeOf(planEntries, option),
        optionKey: targetingOptionKey(option),
        excluded: Boolean(d.negative),
      });
    }
  }
  // Staged names are removable too, so a reviewer can take back an add.
  for (const [key, isExcluded] of [['included', false], ['excluded', true]]) {
    for (const n of (staged?.[key] || [])) {
      const clean = String(n).trim();
      if (clean) push('geo', { label: `${clean}${PENDING_GEO_SUFFIX}`, staged: true, stagedName: clean, excluded: isExcluded });
    }
  }

  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_AUDIENCE_GROUP'])) {
    for (const [groupKey, , s] of audienceSettings(optionDetails(option))) {
      const id = audienceSettingId(s);
      if (!id) continue;
      if (!groupKey.startsWith('included')) continue;
      // Google audiences are the `interests` cell; everything else is `audiences`.
      const field = groupKey === 'includedGoogleAudienceGroup' ? 'interests' : 'audiences';
      push(field, {
        label: String(id),
        targetingType: 'TARGETING_TYPE_AUDIENCE_GROUP',
        optionKey: String(id),
        excluded: false,
      });
    }
  }

  for (const option of optionsOfType(planEntries, ['TARGETING_TYPE_KEYWORD'])) {
    const d = optionDetails(option);
    if (!d.keyword || d.negative) continue;
    push('keywords', {
      label: String(d.keyword),
      targetingType: 'TARGETING_TYPE_KEYWORD',
      optionKey: targetingOptionKey(option),
      excluded: false,
    });
  }

  return entries;
}

/** Which geo targetingType a given option came from. */
function geoTypeOf(planEntries, option) {
  for (const entry of Array.isArray(planEntries) ? planEntries : []) {
    if (GEO_TYPES.includes(entry?.targetingType)
      && (entry.assignedTargetingOptions || []).includes(option)) return entry.targetingType;
  }
  return 'TARGETING_TYPE_GEO_REGION';
}

/**
 * Remove one entry from a line item's plan, returning a NEW plan array.
 *
 * Audiences are pruned per ID from inside the shared AUDIENCE_GROUP option (the
 * whole option covers many ids, so dropping it would remove far more than the
 * one × the reviewer clicked); every other type drops the matching option. An
 * option or group left empty is removed, and a targetingType left with no
 * options goes with it — the launcher rejects an empty createRequest.
 */
export function removeFromPlan(planEntries, { targetingType, optionKey, excluded }) {
  if (!Array.isArray(planEntries)) return planEntries;
  const out = [];
  for (const entry of planEntries) {
    if (entry?.targetingType !== targetingType) { out.push(entry); continue; }

    const options = [];
    for (const option of (entry.assignedTargetingOptions || [])) {
      if (targetingType === 'TARGETING_TYPE_AUDIENCE_GROUP') {
        const details = { ...optionDetails(option) };
        const detailsKey = Object.keys(option)
          .find((k) => k.endsWith('Details') && option[k] && typeof option[k] === 'object');
        let touched = false;
        for (const [groupKey, group] of Object.entries(details)) {
          // ONLY the included groups. targetingPlanEntries offers included ids
          // (the `audiences` cell counts what IS targeted), and real plans carry
          // the same id in both groups with different recency — pruning the
          // exclusion too would BROADEN the copy, letting it reach the cohort the
          // source deliberately excluded. Removing an exclusion is never something
          // the reviewer asked for by clicking × on an included audience.
          if (!groupKey.startsWith('included')) continue;
          const groups = Array.isArray(group) ? group : [group];
          const kept = [];
          for (const g of groups) {
            const settings = (Array.isArray(g?.settings) ? g.settings : [])
              .filter((s) => String(audienceSettingId(s)) !== String(optionKey));
            if (settings.length !== (g?.settings || []).length) touched = true;
            if (settings.length) kept.push({ ...g, settings });
          }
          if (kept.length) details[groupKey] = Array.isArray(group) ? kept : kept[0];
          else delete details[groupKey];
        }
        if (!touched) { options.push(option); continue; }
        if (Object.keys(details).length) options.push({ ...option, [detailsKey]: details });
        continue;
      }
      // Match the SIDE too: a type can hold the same id both positively and as a
      // negative (an include and an exclusion of one market). Dropping both on one
      // × would remove an exclusion the reviewer never touched, widening the copy.
      const sameKey = targetingOptionKey(option) === String(optionKey);
      const sameSide = excluded === undefined
        || Boolean(optionDetails(option).negative) === Boolean(excluded);
      if (!(sameKey && sameSide)) options.push(option);
    }
    if (options.length) out.push({ ...entry, assignedTargetingOptions: options });
  }
  return out;
}

/**
 * Stage a geo ADD for one line item as a geo_plan entry.
 *
 * Names, not ids: only the launcher can resolve a market name to a DV360
 * targetingOptionId (search_geo_region), so the name is carried and resolved at
 * launch. `additive: true` is what makes it safe — _resolve_and_merge_geo keeps
 * the line item's existing GEO_REGION options and appends only these, so an add
 * can never retarget what is already there. A NON-additive spec would replace
 * the whole geo, which is the step-1 override's semantics, not a grid add's.
 */
export function addGeoToPlan(geoPlan, rowId, name, { excluded = false } = {}) {
  const clean = String(name || '').trim();
  if (!clean) return geoPlan;
  const next = { ...(geoPlan || {}) };
  const spec = next[rowId]
    ? { ...next[rowId] }
    : { included: [], excluded: [], additive: true };
  const key = excluded ? 'excluded' : 'included';
  const list = Array.isArray(spec[key]) ? [...spec[key]] : [];
  if (!list.includes(clean)) list.push(clean);
  spec[key] = list;
  spec.included = Array.isArray(spec.included) ? spec.included : [];
  spec.excluded = Array.isArray(spec.excluded) ? spec.excluded : [];
  // Only ever default a NEW entry to additive; never flip an existing spec (a
  // step-1 override arrives non-additive and means "target exactly these").
  if (!next[rowId]) spec.additive = true;
  next[rowId] = spec;
  return next;
}

/** Drop a staged geo name; returns a new geo_plan (or undefined when emptied). */
export function removeGeoFromPlan(geoPlan, rowId, name, { excluded = false } = {}) {
  const spec = geoPlan?.[rowId];
  // `removed` is the caller's signal, NOT object identity. Returning a fresh
  // object for a name that was never staged made every resolved-geo × look like a
  // handled staged-name removal, so the targeting_plan prune was skipped and the
  // market still launched.
  if (!spec) return { geoPlan, removed: false };
  const key = excluded ? 'excluded' : 'included';
  const wanted = String(name).trim().replace(PENDING_GEO_SUFFIX, '').trim();
  const before = Array.isArray(spec[key]) ? spec[key] : [];
  const list = before.filter((n) => String(n).trim() !== wanted);
  if (list.length === before.length) return { geoPlan, removed: false };
  const nextSpec = { ...spec, [key]: list };
  const next = { ...geoPlan };
  if (!(nextSpec.included || []).length && !(nextSpec.excluded || []).length) delete next[rowId];
  else next[rowId] = nextSpec;
  return { geoPlan: Object.keys(next).length ? next : undefined, removed: true };
}

/** The plan for one line item, tolerating a missing/!object sidecar. */
export function planFor(result, lineItemId) {
  const plan = result?.targeting_plan;
  if (!plan || typeof plan !== 'object') return null;
  return plan[String(lineItemId)] || null;
}
